import { useAudioPlayer, useAudioPlayerStatus } from 'expo-audio';
import { useCallback } from 'react';
import { useAssetDownloader } from '@/contexts/DownloadAssetContext';
import { logError } from '@/utils/logHandling';
import { IS_IOS, IS_WEB } from '@/utils/platform';

export function useExerciseSound(url?: string) {
  const player = useAudioPlayer(undefined, { keepAudioSessionActive: true });

  const status = useAudioPlayerStatus(player);
  const isPlaying = IS_WEB
    ? !player.paused && !status?.didJustFinish
    : (status?.playing ?? false);
  const { getLocalUri } = useAssetDownloader();

  const waitUntilLoaded = useCallback(
    (timeoutMs = 5000): Promise<void> =>
      new Promise((resolve) => {
        let timer: ReturnType<typeof setTimeout>;
        const subscription = player.addListener(
          'playbackStatusUpdate',
          (update) => {
            if (update.isLoaded) {
              clearTimeout(timer);
              subscription.remove();
              resolve();
            }
          },
        );
        timer = setTimeout(() => {
          subscription.remove();
          resolve();
        }, timeoutMs);
      }),
    [player],
  );

  const playSound = useCallback(async () => {
    if (!url) {
      return;
    }

    try {
      const uri = await getLocalUri(url);
      player.replace({ uri });
      if (IS_IOS) {
        await waitUntilLoaded();
      }

      player.play();
    } catch (error) {
      logError('Could not load or play audio', error);
    }
  }, [player, getLocalUri, url, waitUntilLoaded]);

  const stopSound = useCallback(async () => {
    if (!player.isLoaded) {
      return;
    }
    try {
      player.pause();
      await player.seekTo(0);
    } catch (error) {
      logError('Could not stop audio playing', error);
    }
  }, [player]);

  return { playSound, stopSound, isPlaying };
}
